import { useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { getMediaList, postMediaDelete, postMediaUpdate, postMediaInsert } from './../utils/api';
import Dropzone from 'react-dropzone';
import classNames from 'classnames';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Delete from '@mui/icons-material/Delete';

import Loading from './loading';
import MediaModal from './media-modal';

const drawerWidth = 240;

const Media = () => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [saving, setSaving] = useState(false);
  const [media, setMedia] = useState([]);
  const [refresh, setRefresh] = useState(true);
  const [showMediaModal, setShowMediaModal] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => setAccessToken(token));
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (!isLoading && refresh && accessToken) {
      getMediaList(accessToken).then((data) => {
        setRefresh(false);
        setSaving(false);
        setMedia(data);
      });
    }
  }, [refresh, isLoading, accessToken]);

  function onDrop(files) {
    setSaving(true);
    const file = files[0];
    postMediaInsert(accessToken, file.name, file).then(() => {
      setRefresh(true);
    })
    .catch((error) => {
      console.warn(error);
      alert(error.toString());
    });
  }

  function toggleHidden(id) {
    const newMedia = [...media];
    const ix = newMedia.findIndex(m => m.id === id);
    if (ix !== -1) {
      newMedia[ix].hidden = !newMedia[ix].hidden;
      // Optimistic update
      setMedia(newMedia);
      
      postMediaUpdate(accessToken, newMedia[ix].id, newMedia[ix].name, newMedia[ix].hidden, newMedia[ix].carouselMedia)
      .then(() => {
        setRefresh(true);
      })
      .catch((error) => {
        console.warn(error);
        alert(error.toString());
      });
    }
  }

  function toggleCarouselMedia(id) {
    const newMedia = [...media];
    const ix = newMedia.findIndex(m => m.id === id);
    if (ix !== -1) {
      newMedia[ix].carouselMedia = !newMedia[ix].carouselMedia;
      // Optimistic update
      setMedia(newMedia);

      postMediaUpdate(accessToken, newMedia[ix].id, newMedia[ix].name, newMedia[ix].hidden, newMedia[ix].carouselMedia)
      .then(() => {
        setRefresh(true);
      })
      .catch((error) => {
        console.warn(error);
        alert(error.toString());
      });
    }
  }

  function openMediaModal(m) {
    setShowMediaModal(m);
  }

  function closeMediaModal() {
    setShowMediaModal(null);
    setRefresh(true);
  }

  function deleteMedia(m) {
    if (confirm('Are you sure you want to delete image? This cannot be undone!')) {
      postMediaDelete(accessToken, m.id, m.name)
      .then(() => {
        setRefresh(true);
      })
      .catch((error) => {
        console.warn(error);
        alert(error.toString());
      });
    }
  }

  if (isLoading || saving || refresh) {
    return <Loading/>;
  }

  return (
    <span>
      <MediaModal
        accessToken={accessToken}
        open={!!showMediaModal}
        id={showMediaModal ? showMediaModal.id : 0}
        name={showMediaModal ? showMediaModal.name : ""}
        hidden={showMediaModal ? showMediaModal.hidden : 0}
        carouselMedia={showMediaModal ? showMediaModal.carouselMedia : 0}
        onClose={closeMediaModal}/>
      
      <Dropzone onDrop={onDrop} accept={{'image/*': []}}>
        {({getRootProps, getInputProps, isDragActive}) => {
          return (
            <div
              {...getRootProps()}
              className={classNames('dropzone', {'dropzone--isActive': isDragActive})}
              style={{padding: '15px', borderWidth: '1px', borderColor: '#666', borderStyle: 'dashed', borderRadius: '5px', backgroundColor: 'white'}}
            >
              <input {...getInputProps()}/>
              {
                isDragActive ?
                  <p>Drop image here...</p> :
                  <p>Drop image here, or click to select files to upload.</p>
              }
            </div>
          )
        }}
      </Dropzone>

      <Box sx={{
        overflow: 'auto',
        maxHeight: (theme) => `calc(100vh - ${theme.spacing(14)})`,
        maxWidth: {
            xs: (theme) => `calc(100vw - ${theme.spacing(6)})`,
            md: (theme) => `calc(100vw - ${theme.spacing(6)} + ${drawerWidth}px)`
        }
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Created</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Carousel-image</TableCell>
              <TableCell>Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {media.map(m => {
              return (
                <TableRow key={m.id}>
                  <TableCell>{m.created}</TableCell>
                  <TableCell><Button onClick={() => openMediaModal(m)} color="primary">{m.name}</Button></TableCell>
                  <TableCell><Switch checked={!m.hidden} value="Active" color="primary" onChange={() => toggleHidden(m.id)} /></TableCell>
                  <TableCell><Switch checked={m.carouselMedia} value="Active" color="primary" onChange={() => toggleCarouselMedia(m.id)} /></TableCell>
                  <TableCell><Button disabled={!m.hidden} onClick={() => deleteMedia(m)} color="secondary"><Delete /></Button></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </span>
  );
}

export default Media;